import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { CiaoSharedModule } from '~app/components/shared/shared.module';

/**
 * ag-grid cell renderer for the crew-member grid's person columns
 * (Crew Member / POC / Additional POC).
 *
 * Renders `<name> <guest-badge?> <phone?>` so the "G" badge sits between the
 * person's name and their phone number per the design spec, while preserving
 * the existing layout when the person isn't a guest.
 *
 * Guest detection uses the person's id (not their scoped `AssignedRoles`)
 * because the role list shipped down on a crew row is filtered to that team
 * and would miss a guest assignment in any other team.
 *
 * When the referenced person has been deleted, the row still carries the id
 * (e.g. `crewMemberId`) but the joined person object is gone. In that case we
 * render a greyed-out, non-interactive "Deleted user" placeholder instead of a
 * blank cell, so trip history stays readable.
 */
@Component({
  selector: 'ciao-person-name-cell-renderer',
  standalone: true,
  imports: [CommonModule, CiaoSharedModule],
  template: `
    @if (person) {
      <span class="person-name">{{ person.displayName }}</span>
      @if (person.id) {
        <ciao-guest-badge
          [personId]="person.id"
          variant="table"
        ></ciao-guest-badge>
      }
      @if (phone) {
        <span class="person-phone">&nbsp;&nbsp;{{ phone }}</span>
      }
    } @else if (deleted) {
      <span class="deleted-user">Deleted user</span>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }
      .person-name {
        white-space: nowrap;
      }
      .person-phone {
        white-space: nowrap;
      }
      .deleted-user {
        color: #999;
        font-style: italic;
        white-space: nowrap;
      }
    `,
  ],
})
export class PersonNameCellRendererComponent
  implements ICellRendererAngularComp
{
  person?: {
    id?: string;
    displayName?: string;
    defaultPhone?: { phoneNumber?: string };
  };
  phone?: string;
  /** The referenced person id is present but the person object is gone — i.e.
   * the user was deleted. Drives the greyed-out "Deleted user" placeholder. */
  deleted = false;

  agInit(params: ICellRendererParams): void {
    this.read(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.read(params);
    return true;
  }

  private read(params: ICellRendererParams): void {
    const field = params?.colDef?.field;
    this.person = field ? params?.data?.[field] : null;
    this.phone = this.person?.defaultPhone?.phoneNumber ?? '';
    // Person columns pair a `<field>` object with a `<field>Id`; a present id
    // with no object means the person row was deleted out from under it.
    const referencedId = field ? params?.data?.[`${field}Id`] : null;
    this.deleted = !this.person && !!referencedId;
  }
}
